// react
import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import 'devtools-detect';

// mui
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// recoil
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

// components
import { Gateway, ErrorBody, requireLogin, LoginRequiredDialog } from './components';

// theme
import { DarkTheme } from '@goldblack/client-comps';

// api
import { IsAuth, FilterGetOption, Setting } from './api';
import { debounceFlush } from './request-control/debounce';

import { handleResponse, handleError, authencated } from './api/internal/api-error';

import ErrorBoundary from './error/error-boundary';
import { AppLoading } from './components/app-loading';

// storage
import { LocalStorageState, TransientState } from './storage/index.js';

// client-shared
import { api } from '@goldblack/client-api';
import {
    comp,
    ErrorPopup,
    InfoPopup,
    WarningPopup,
    SuccessPopup,
    onDevToolsChange,
    onContextMenu,
} from '@goldblack/client-comps';
import { storage } from '@goldblack/client-storage';
import { config } from '@goldblack/client-config';

// css (global)
import './app.css';
import { Filter } from '@mui/icons-material';

// visibility change
let currentMute = false;

function MyRoutes() {
    return (
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
                <Route exact path='/' element={<Gateway />} />
                <Route path='*' element={<Navigate to='/' />} />
            </Routes>
        </BrowserRouter>
    );
}

// Calls a logged-out guest may make while browsing carry `guest: true` in their axios config (see src/api; the server
// side is the `access: 'guest'` rows of apps/loise/server/routes.js).
// Any other request from a browsing guest is an action (follow, like, comment, message...):
// drop it and ask for login. The action is not repeated after login; the user does it again.
function guestGate(req) {
    const { user: userSession, path } = storage.transience.getValue();
    if (userSession || path.gateway !== 'main' || req.guest) return req;
    requireLogin();
    return Promise.reject(new Error(`login required: ${req.url}`));
}

function onVisibilityChange(ev) {
    const internal = storage.transience.getValue('internal');
    const currentState = document.visibilityState;

    // 뮤트 로직
    if (currentState === 'hidden') {
        debounceFlush(); // send the debounced likes still waiting now: the page may not come back
        currentMute = internal.mute;
        storage.transience.setState('internal.mute', true);
    } else {
        storage.transience.setState('internal.mute', currentMute);
    }
}

export default function App() {
    const [appData, setAppData] = useState({ loading: true });

    // initialize client-shared
    const tvalue = useRecoilValue(TransientState);
    const tset = useSetRecoilState(TransientState);
    const treset = useResetRecoilState(TransientState);
    const lvalue = useRecoilValue(LocalStorageState);
    const lset = useSetRecoilState(LocalStorageState);
    const lreset = useResetRecoilState(LocalStorageState);

    storage.load({ tvalue, tset, treset, lvalue, lset, lreset });

    useEffect(() => {
        const configs = config.load('loise');
        api.load({ handleResponse, handleError, authencated, transience: storage.transience });
        api.axios.interceptors.request.use(guestGate);
        comp.load({ transience: storage.transience, FilterGetOption, Setting });

        window.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', debounceFlush);
        window.addEventListener('devtoolschange', onDevToolsChange);
        window.addEventListener('contextmenu', onContextMenu);

        const segments = window.location.pathname.split('/').filter(Boolean);
        if (segments.length > 0) {
            storage.transience.setState('path.deeplink', segments);
        }

        const userSession = storage.transience.getValue('user');
        if (!userSession) {
            const params = new URLSearchParams(window.location.search);
            if (params.get('token')) {
                window.history.replaceState({}, '', window.location.pathname);
            }
            IsAuth(configs.meta.hash, setAppData);
        }

        return () => {
            window.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', debounceFlush);
            window.removeEventListener('devtoolschange', onDevToolsChange);
            window.removeEventListener('contextmenu', onContextMenu);
        };
    }, []);

    return (
        <ThemeProvider theme={DarkTheme}>
            <CssBaseline />
            <ErrorBoundary fallback={ErrorBody}>{appData.loading ? <AppLoading /> : <MyRoutes />}</ErrorBoundary>

            {/*===== global popups =====*/}
            <ErrorPopup />
            <WarningPopup />
            <InfoPopup />
            <SuccessPopup />
            <LoginRequiredDialog />
        </ThemeProvider>
    );
}
